import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  addDays,
  fmtDay,
  fmtDow,
  fmtISO,
  isSameDay,
} from '../lib/format.js';
import { LOG_WINDOW_DAYS } from '../lib/reviewWindow.js';

// v2.0 — single-codepoint flag glyphs for the supported countries.
// Kept inline (not in lib/) because they're the only place we use country
// codes as a *visual* affordance. If we ever add a third country, this
// graduates to a tiny module.
const FLAG_EMOJI = { PH: '🇵🇭', PL: '🇵🇱' };

export function DateBar({
  selected,
  onSelect,
  hideWeekends,
  selectedDayMins,
  summaryByDate = {},
  holidayByDate = null, // Map<isoDate, {name, kind}> for the user's country
  holidayCountry = '',
  // v2.6 — the bar renders the last `viewDays` days ending today. It starts
  // at 35 (the logging window) and grows when the user clicks "Load older"
  // to *review* entries further back. Logging stays capped at 35 days
  // elsewhere; this only widens what's viewable. `canLoadOlder` gates the
  // paging control (false once the review floor — employment start — is
  // reached).
  viewDays = LOG_WINDOW_DAYS,
  onLoadOlder,
  canLoadOlder = false,
}) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const days = useMemo(() => {
    const all = Array.from({ length: viewDays }, (_, i) =>
      addDays(today, -(viewDays - 1) + i),
    );
    // v2.0 — "Hide non-working days" also hides public holidays for the
    // user's team. We treat the holiday map as authoritative: if the
    // server says a date is non-working, the toggle hides it the same
    // way as Saturday or Sunday.
    return hideWeekends
      ? all.filter((d) => {
          const w = d.getDay();
          if (w === 0 || w === 6) return false;
          if (holidayByDate && holidayByDate.has(fmtISO(d))) return false;
          return true;
        })
      : all;
  }, [hideWeekends, today, holidayByDate, viewDays]);
  const ref = useRef(null);
  const selRef = useRef(null);
  // Set the tick before an "Load older" bump so the layout effect below can
  // reveal the newly-prepended (left-hand) chips instead of staying pinned
  // to today.
  const pendingOlder = useRef(false);
  // Anchor to today (right edge) on mount and whenever the weekend filter
  // flips — both change which chip sits at the far right.
  useEffect(() => {
    if (ref.current) ref.current.scrollLeft = ref.current.scrollWidth;
  }, [hideWeekends]);
  // After "Load older" grows the window, jump to the left edge so the days
  // just revealed (and the paging control) are what the user sees.
  useLayoutEffect(() => {
    if (pendingOlder.current && ref.current) {
      ref.current.scrollLeft = 0;
      pendingOlder.current = false;
    }
  }, [viewDays]);
  // Keep the selected chip in view when it changes — e.g. arrow-key nav into
  // an older day that had scrolled off-screen. `nearest` won't disturb the
  // scroll when the chip is already visible.
  useEffect(() => {
    selRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selected]);
  const handleLoadOlder = () => {
    pendingOlder.current = true;
    onLoadOlder?.();
  };
  return (
    <div ref={ref} className="tt-datebar">
      {canLoadOlder && (
        <button
          type="button"
          className="tt-datechip tt-datechip-more"
          onClick={handleLoadOlder}
          title="Show older days for review"
          aria-label="Load older days"
        >
          <span className="tt-datechip-dow" aria-hidden="true">
            ‹
          </span>
          <span className="tt-datechip-more-label">Older</span>
          <span className="tt-datechip-mark" />
        </button>
      )}
      {days.map((d) => {
        const iso = fmtISO(d);
        const isSel = isSameDay(d, selected);
        const isToday = isSameDay(d, today);
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const holiday = holidayByDate ? holidayByDate.get(iso) : null;
        // Prefer the live total for the selected day (reflects in-memory
        // edits before the summary refetches); fall back to the day-total
        // summary map for everything else.
        const mins = isSel
          ? selectedDayMins
          : summaryByDate[iso] || 0;
        const empty = mins === 0;
        return (
          <button
            key={iso}
            ref={isSel ? selRef : null}
            onClick={() => onSelect(d)}
            // v2.0 — holiday chips reuse the weekend "muted" styling so
            // they read as non-working at a glance, with a country flag
            // glyph layered on top. Tooltip carries the holiday name so
            // hover discloses what's happening without growing the chip.
            title={holiday ? `${holiday.name} (${holidayCountry})` : undefined}
            className={
              'tt-datechip ' +
              (isSel ? 'is-selected ' : '') +
              (isToday && !isSel ? 'is-today ' : '') +
              ((isWeekend || holiday) ? 'is-weekend ' : '')
            }
          >
            <span className="tt-datechip-dow">{fmtDow(d)}</span>
            <span className="tt-datechip-day">{fmtDay(d)}</span>
            {holiday ? (
              <span className="tt-datechip-mark" aria-hidden="true">
                {FLAG_EMOJI[holidayCountry] || '•'}
              </span>
            ) : (
              <span
                className={'tt-datechip-mark ' + (empty ? 'is-empty' : '')}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
